import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import dynamic, { LoaderComponent } from 'next/dynamic';

import { KayWoodProps } from '~/features/brand-effects/components/KayWood/KayWood';
import { useActiveBrandEffect } from '~/features/brand-effects/hooks/useActiveBrandEffect';
import { random } from '~/features/brand-effects/util/random';
import { ProductCard } from '~/features/products/components/ProductCard/ProductCard';
import { useProductViewModuleTracking } from '~/features/tracking/hooks/useProductViewModuleTracking';
import ArrowLeftIcon from '~/icons/chevron-left.svg';
import ArrowRightIcon from '~/icons/chevron-right.svg';
import { IM50ShopTheLookModule } from '~/lib/data-contract';
import { CarouselProps } from '~/shared/components/Carousel/Carousel';
import { DynamicCallToAction } from '~/shared/components/DynamicCallToAction/DynamicCallToAction';
import { Icon } from '~/shared/components/Icon/Icon';
import { ModuleHeader } from '~/shared/components/ModuleHeader/ModuleHeader';
import { RawHtml } from '~/shared/components/RawHtml/RawHtml';
import { VisuallyHidden } from '~/shared/components/VisuallyHidden/VisuallyHidden';
import { isSSR } from '~/shared/utils/platform/utils/platform';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer/ModuleContainer';
import { useModuleColors } from '~/theme';

import { TextMediaCanvas } from './components/TextMediaCanvas/TextMediaCanvas';
import { TextMediaContainer } from './components/TextMediaContainer';
import { TextMediaOffsetContent } from './components/TextMediaOffsetContent/TextMediaOffsetContent';
import styles from './m50ShopTheLook.module.css';

const KayWood = dynamic(() => import('~/features/brand-effects/components/KayWood/KayWood').then((module) => module.KayWood) as LoaderComponent<KayWoodProps>, { ssr: false });

const Carousel = dynamic(() => import('~/shared/components/Carousel/Carousel').then((module) => module.Carousel) as LoaderComponent<CarouselProps>, { ssr: false });

export type M50ShopTheLookProps = IM50ShopTheLookModule;

export const M50ShopTheLook = ({ id, moduleBackgroundColor, headline, label, callToAction, text, image, video, items, brandEffect, type, anchorId }: M50ShopTheLookProps) => {
	const { textColor, backgroundColor } = useModuleColors(moduleBackgroundColor);
	const activeBrandEffect = useActiveBrandEffect(brandEffect);
	const { moduleRef } = useProductViewModuleTracking(items, {
		listId: id,
		listName: anchorId,
	});

	const { translate } = useTranslation();

	const kayBojesenEffectOnImage = useMemo(() => {
		return Boolean(activeBrandEffect === 'kay' && image && random() > 0.5);
	}, [activeBrandEffect, image]);

	const prevButtonRef = useRef<HTMLButtonElement>(null);
	const nextButtonRef = useRef<HTMLButtonElement>(null);

	const ref = useRef<HTMLDivElement>(null);
	const [offsetContentHeight, setOffsetContentHeight] = useState<number>(0);

	const useIsomorphicLayoutEffect = isSSR ? useEffect : useLayoutEffect;

	useIsomorphicLayoutEffect(() => {
		const handler = () => {
			if (ref.current) {
				setOffsetContentHeight(ref.current.offsetHeight);
			}
		};

		window.addEventListener('resize', handler);
		handler();

		return () => {
			window.removeEventListener('resize', handler);
		};
	}, []);

	const isWithProducts = items.length > 0;
	const isWithBackground = Boolean(backgroundColor);

	return (
		<div className={styles.container}>
			<ModuleContainer
				id={id}
				anchorId={anchorId?.toLowerCase()}
				moduleBackgroundColor={backgroundColor}
				brandEffect={brandEffect}
				moduleType={type}
				className={clsx(styles.containerInner, {
					[styles.withOffsetContent]: isWithProducts,
					[styles.withBackground]: isWithBackground,
				})}
			>
				<div ref={moduleRef} />
				<TextMediaContainer
					position="center"
					textColor={textColor}
					offsetContentHeight={offsetContentHeight}
					imageFirstOnMobile={true}
				>
					{activeBrandEffect === 'kay' && kayBojesenEffectOnImage ? (
						<KayWood
							randomizePosition={{ x: 0.1, y: -0.4 }}
							colorToComplement={backgroundColor}
							top={'-10%'}
							left={'66%'}
						/>
					) : null}
					<div className={styles.textMediaContent}>
						{activeBrandEffect === 'kay' && !kayBojesenEffectOnImage ? (
							<KayWood
								randomizePosition={{ x: 0.1, y: -0.4 }}
								colorToComplement={backgroundColor}
								className={styles.effect}
							/>
						) : null}
						{headline ? (
							<div className={styles.moduleHeadline}>
								<ModuleHeader
									label={label}
									headline={headline}
								/>
							</div>
						) : null}
						{text ? (
							<RawHtml
								maxWidth="530px"
								html={text.html}
							/>
						) : null}
						{callToAction ? (
							<DynamicCallToAction
								className={styles.callToAction}
								callToAction={callToAction}
								defaultStyle="arrow"
							/>
						) : null}
					</div>
					{image || video ? (
						<TextMediaCanvas
							video={video}
							image={image}
							position="center"
						/>
					) : null}
					{isWithProducts ? (
						<div className={styles.controls}>
							<button
								ref={prevButtonRef}
								className={styles.controlButton}
								type="button"
							>
								<Icon size="lg">
									<ArrowLeftIcon />
								</Icon>
								<VisuallyHidden>{translate('product.carousel.previous')}</VisuallyHidden>
							</button>
							<button
								ref={nextButtonRef}
								className={styles.controlButton}
								type="button"
							>
								<Icon size="lg">
									<ArrowRightIcon />
								</Icon>
								<VisuallyHidden>{translate('product.carousel.next')}</VisuallyHidden>
							</button>
						</div>
					) : null}
					{isWithProducts ? (
						<TextMediaOffsetContent ref={ref}>
							<Carousel
								prevRef={prevButtonRef}
								nextRef={nextButtonRef}
								items={items.map((item, i) => (
									<ProductCard
										className={styles.productCard}
										product={item}
										index={i}
										listName={anchorId}
										listId={id}
									/>
								))}
							/>
						</TextMediaOffsetContent>
					) : null}
				</TextMediaContainer>
			</ModuleContainer>
		</div>
	);
};
